import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../../supabaseClient';
import { FaPlus, FaTimes, FaExclamationTriangle } from 'react-icons/fa';

// Custom Dropdown Component
const CustomDropdown = ({ value, onChange, options, placeholder, disabled, name, isArabic }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const selectedOption = options.find(opt => opt.value === value);
  const displayText = selectedOption ? selectedOption.label : placeholder;

  return (
    <div ref={dropdownRef} style={{ position: 'relative', width: '100%' }}>
      <div
        onClick={() => !disabled && setIsOpen(!isOpen)}
        style={{
          width: '100%',
          padding: '12px 16px',
          border: isOpen ? '2px solid #ff7a1a' : '1px solid #e5e7eb',
          borderRadius: '12px',
          background: disabled ? '#f9fafb' : 'white',
          color: value ? '#1f2937' : '#9ca3af',
          cursor: disabled ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          minHeight: '44px',
          transition: 'all 0.2s'
        }}
        onMouseEnter={(e) => {
          if (!disabled && !isOpen) {
            e.currentTarget.style.borderColor = '#d1d5db';
          }
        }}
        onMouseLeave={(e) => {
          if (!disabled && !isOpen) {
            e.currentTarget.style.borderColor = '#e5e7eb';
          }
        }}
      >
        <span>{displayText}</span>
        <svg 
          width="16" 
          height="16" 
          viewBox="0 0 24 24" 
          fill="none" 
          stroke={isOpen ? '#ff7a1a' : '#6b7280'} 
          strokeWidth="2"
          style={{
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s'
          }}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </div>
      {isOpen && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            left: 0,
            right: 0,
            background: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '12px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            zIndex: 1000,
            maxHeight: '220px', // Show approximately 5 options (44px per option)
            overflowY: 'auto',
            padding: '8px'
          }}
        >
          {options.map((option) => {
            const isSelected = value === option.value;
            return (
              <div
                key={option.value}
                onClick={() => {
                  onChange({ target: { name, value: option.value } });
                  setIsOpen(false);
                }}
                style={{
                  padding: '12px 16px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  background: isSelected ? '#fff4e6' : 'transparent',
                  color: isSelected ? '#ff7a1a' : '#1f2937',
                  fontWeight: isSelected ? '600' : '400',
                  fontSize: '15px',
                  transition: 'all 0.15s',
                  marginBottom: '4px'
                }}
                onMouseEnter={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.background = '#f9fafb';
                    e.currentTarget.style.color = '#1f2937';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = '#1f2937';
                  }
                }}
              >
                {option.label}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const LANGUAGES = ['Arabic', 'English', 'French', 'Spanish', 'German', 'Italian', 'Portuguese', 'Russian', 'Chinese', 'Japanese', 'Korean', 'Hindi', 'Turkish'];
const LANGUAGE_LEVELS = ['Native', 'Fluent', 'Advanced', 'Intermediate', 'Beginner'];
const SUBJECTS = [
  'Mathematics', 'Physics', 'Chemistry', 'Biology', 'Science', 
  'English', 'Arabic', 'French', 'Spanish', 'History', 'Geography', 
  'Computer Science', 'Programming', 'Art', 'Music', 'Physical Education', 'Other'
];

const LANGUAGES_AR = ['العربية', 'الإنجليزية', 'الفرنسية', 'الإسبانية', 'الألمانية', 'الإيطالية', 'البرتغالية', 'الروسية', 'الصينية', 'اليابانية', 'الكورية', 'الهندية', 'التركية'];
const LANGUAGE_LEVELS_AR = ['أصلي', 'طلاقة', 'متقدم', 'متوسط', 'مبتدئ'];
const SUBJECTS_AR = [
  'الرياضيات', 'الفيزياء', 'الكيمياء', 'الأحياء', 'العلوم',
  'الإنجليزية', 'العربية', 'الفرنسية', 'الإسبانية', 'التاريخ', 'الجغرافيا',
  'علوم الحاسوب', 'البرمجة', 'الفن', 'الموسيقى', 'التربية البدنية', 'أخرى'
];

// Subject sub-categories
const SUBJECT_SUBCATEGORIES = {
  'Mathematics': [
    'Algebra', 'Geometry', 'Calculus', 'Statistics', 'Trigonometry', 
    'Number Theory', 'Linear Algebra', 'Discrete Mathematics', 'Applied Mathematics'
  ],
  'Physics': [
    'Mechanics', 'Thermodynamics', 'Electromagnetism', 'Optics', 'Quantum Physics', 'Astrophysics'
  ],
  'Chemistry': [
    'Organic Chemistry', 'Inorganic Chemistry', 'Physical Chemistry', 'Biochemistry', 'Analytical Chemistry'
  ],
  'Biology': [
    'Cell Biology', 'Molecular Biology', 'Genetics', 'Ecology', 'Anatomy', 'Physiology'
  ],
  'English': [
    'Literature', 'Grammar', 'Writing', 'Reading Comprehension', 'ESL/EFL', 'Creative Writing'
  ],
  'Computer Science': [
    'Data Structures', 'Algorithms', 'Database Systems', 'Software Engineering', 'Web Development'
  ],
  'Programming': [
    'Python', 'Java', 'JavaScript', 'C++', 'C#', 'PHP', 'Ruby', 'Swift', 'Kotlin'
  ]
};

const SUBJECT_SUBCATEGORIES_AR = {
  'Mathematics': [
    'الجبر', 'الهندسة', 'التفاضل والتكامل', 'الإحصاء', 'علم المثلثات',
    'نظرية الأعداد', 'الجبر الخطي', 'الرياضيات المتقطعة', 'الرياضيات التطبيقية'
  ],
  'Physics': [
    'الميكانيكا', 'الديناميكا الحرارية', 'الكهرومغناطيسية', 'البصريات', 'فيزياء الكم', 'الفيزياء الفلكية'
  ],
  'Chemistry': [
    'الكيمياء العضوية', 'الكيمياء غير العضوية', 'الكيمياء الفيزيائية', 'الكيمياء الحيوية', 'الكيمياء التحليلية'
  ],
  'Biology': [
    'بيولوجيا الخلية', 'البيولوجيا الجزيئية', 'علم الوراثة', 'علم البيئة', 'التشريح', 'علم وظائف الأعضاء'
  ],
  'English': [
    'الأدب', 'القواعد', 'الكتابة', 'فهم القراءة', 'الإنجليزية كلغة ثانية', 'الكتابة الإبداعية'
  ],
  'Computer Science': [
    'هياكل البيانات', 'الخوارزميات', 'أنظمة قواعد البيانات', 'هندسة البرمجيات', 'تطوير الويب'
  ],
  'Programming': [
    'Python', 'Java', 'JavaScript', 'C++', 'C#', 'PHP', 'Ruby', 'Swift', 'Kotlin'
  ]
};

// Grade levels
const SCHOOL_GRADES = Array.from({ length: 12 }, (_, i) => `Grade ${i + 1}`);
const UNIVERSITY_GRADES = ['Foundation', 'Diploma', 'Bachelor', 'Master', 'PhD'];
const SCHOOL_GRADES_AR = Array.from({ length: 12 }, (_, i) => `الصف ${i + 1}`);
const UNIVERSITY_GRADES_AR = ['سنة تأسيسية', 'دبلوم', 'بكالوريوس', 'ماجستير', 'دكتوراه'];

export default function Step1About({ wizardData, onNext, onBack, onSave, translations, isArabic, language }) {
  const t = translations || {};
  
  const [formData, setFormData] = useState({
    full_name: wizardData.full_name || '',
    email: wizardData.email || '',
    phone: wizardData.phone || '',
    date_of_birth_day: wizardData.date_of_birth_day || '',
    date_of_birth_month: wizardData.date_of_birth_month || '',
    date_of_birth_year: wizardData.date_of_birth_year || '',
    gender: wizardData.gender || '',
    subject: wizardData.subject || (wizardData.subjects && wizardData.subjects.length > 0 ? wizardData.subjects[0] : ''),
    subjects: wizardData.subjects || (wizardData.subject ? [wizardData.subject] : []),
    subject_subcategories: wizardData.subject_subcategories || [],
    other_subject_names: wizardData.other_subject_names || {}, // Map of subject index to custom name
    languages: wizardData.languages || [],
    grade_levels: wizardData.grade_levels || [],
    over_18_confirmed: wizardData.over_18_confirmed || false
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(null);
  const [gradesDropdownOpen, setGradesDropdownOpen] = useState(false);
  const gradesDropdownRef = useRef(null);

  // Load user data on mount
  useEffect(() => {
    const loadUser = async () => {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (authUser) {
        setUser(authUser);
        if (!formData.email) {
          setFormData(prev => ({ ...prev, email: authUser.email || '' }));
        }
      }
    };
    loadUser();
  }, []);

  // Close grades dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (gradesDropdownRef.current && !gradesDropdownRef.current.contains(event.target)) {
        setGradesDropdownOpen(false);
      }
    };
    if (gradesDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [gradesDropdownOpen]);

  // Helper function to calculate age from date of birth
  const calculateAge = (day, month, year) => {
    if (!day || !month || !year) return null;
    
    const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const monthIndex = MONTHS.indexOf(month);
    if (monthIndex === -1) return null;
    
    const birthDate = new Date(parseInt(year), monthIndex, parseInt(day));
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    return age;
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.full_name || formData.full_name.trim() === '') {
      newErrors.full_name = isArabic ? `${t.fullName} ${t.required}` : `${t.fullName} ${t.required}`;
    }

    if (!formData.email || formData.email.trim() === '') {
      newErrors.email = isArabic ? `${t.emailAddress} ${t.required}` : `${t.emailAddress} ${t.required}`;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t.validEmail || 'Please enter a valid email address';
    }

    if (!formData.phone || formData.phone.trim() === '') {
      newErrors.phone = isArabic ? `${t.phoneNumber} ${t.required}` : `${t.phoneNumber} ${t.required}`;
    }

    if (!formData.date_of_birth_day || !formData.date_of_birth_month || !formData.date_of_birth_year) {
      newErrors.date_of_birth_day = isArabic ? 'تاريخ الميلاد مطلوب' : 'Date of birth is required';
    } else {
      // Validate age (must be 18 or older)
      const age = calculateAge(formData.date_of_birth_day, formData.date_of_birth_month, formData.date_of_birth_year);
      if (age !== null && age < 18) {
        newErrors.date_of_birth_day = isArabic ? 'يجب أن يكون عمرك 18 عاماً أو أكثر' : 'You must be 18 years or older';
      }
    }

    if (!formData.gender || formData.gender === '') {
      newErrors.gender = isArabic ? 'الجنس مطلوب' : 'Gender is required';
    }

    if (!formData.subjects || formData.subjects.length === 0) {
      newErrors.subjects = isArabic ? `${t.subjectYouTeach} ${t.required}` : `${t.subjectYouTeach} ${t.required}`;
    } else if (formData.subjects.length > 3) {
      newErrors.subjects = isArabic ? 'يمكنك اختيار 3 مواد كحد أقصى' : 'You can select a maximum of 3 subjects';
    }
    
    // Validate subcategories if subject has them
    formData.subjects.forEach((subject, idx) => {
      if (SUBJECT_SUBCATEGORIES[subject] && (!formData.subject_subcategories || !formData.subject_subcategories[idx] || formData.subject_subcategories[idx].length === 0)) {
        newErrors[`subject_subcategory_${idx}`] = isArabic 
          ? `يرجى تحديد نوع ${isArabic ? SUBJECTS_AR[SUBJECTS.indexOf(subject)] : subject}`
          : `Please specify the type of ${subject}`;
      }
      
      // Validate "Other" subject requires custom name
      if (subject === 'Other') {
        const otherName = formData.other_subject_names?.[idx];
        if (!otherName || otherName.trim() === '') {
          newErrors[`other_subject_${idx}`] = isArabic 
            ? 'يرجى إدخال اسم المادة'
            : 'Please enter the subject name';
        }
      }
    });

    if (!formData.languages || formData.languages.length === 0) {
      newErrors.languages = t.atLeastOneLanguage || 'At least one language is required';
    }

    if (!formData.grade_levels || formData.grade_levels.length === 0) {
      newErrors.grade_levels = isArabic ? 'يجب اختيار مستوى واحد على الأقل' : 'At least one grade level is required';
    }

    if (!formData.over_18_confirmed) {
      newErrors.over_18_confirmed = t.mustConfirmOver18 || 'You must confirm you are over 18';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleSubjectToggle = (subject) => {
    setFormData(prev => {
      const currentSubjects = prev.subjects || [];
      const subjectIndex = currentSubjects.indexOf(subject);
      
      if (subjectIndex > -1) {
        // Remove subject
        const newSubjects = currentSubjects.filter(s => s !== subject);
        const newSubcategories = (prev.subject_subcategories || []).filter((_, idx) => idx !== subjectIndex);
        const newOtherNames = { ...(prev.other_subject_names || {}) };
        // Remove other subject name for this index and reindex
        const updatedOtherNames = {};
        newSubjects.forEach((s, newIdx) => {
          const oldIdx = currentSubjects.indexOf(s);
          if (oldIdx !== -1 && prev.other_subject_names?.[oldIdx]) {
            updatedOtherNames[newIdx] = prev.other_subject_names[oldIdx];
          }
        });
        delete newOtherNames[subjectIndex];
        return {
          ...prev,
          subjects: newSubjects,
          subject_subcategories: newSubcategories,
          other_subject_names: updatedOtherNames,
          subject: newSubjects.length > 0 ? newSubjects[0] : '' // Keep first subject for backward compatibility
        };
      } else {
        // Add subject (max 3)
        if (currentSubjects.length >= 3) {
          return prev; // Don't add if already at max
        }
        const newSubjects = [...currentSubjects, subject];
        const newSubcategories = [...(prev.subject_subcategories || []), []];
        return {
          ...prev,
          subjects: newSubjects,
          subject_subcategories: newSubcategories,
          subject: newSubjects[0] // Keep first subject for backward compatibility
        };
      }
    });
    
    // Clear errors
    if (errors.subjects) {
      setErrors(prev => ({ ...prev, subjects: null }));
    }
  };

  const handleOtherSubjectNameChange = (subjectIndex, value) => {
    setFormData(prev => ({
      ...prev,
      other_subject_names: {
        ...(prev.other_subject_names || {}),
        [subjectIndex]: value
      }
    }));
    
    // Clear error for this field
    if (errors[`other_subject_${subjectIndex}`]) {
      setErrors(prev => ({ ...prev, [`other_subject_${subjectIndex}`]: null }));
    }
  };

  const handleSubcategoryToggle = (subjectIndex, subcategory) => {
    setFormData(prev => {
      const newSubcategories = [...(prev.subject_subcategories || [])];
      if (!newSubcategories[subjectIndex]) {
        newSubcategories[subjectIndex] = [];
      }
      
      const currentSubcats = newSubcategories[subjectIndex];
      const subcatIndex = currentSubcats.indexOf(subcategory);
      
      if (subcatIndex > -1) {
        // Remove subcategory
        newSubcategories[subjectIndex] = currentSubcats.filter(s => s !== subcategory);
      } else {
        // Add subcategory (max 3)
        if (currentSubcats.length >= 3) {
          return prev; // Don't add if already at max
        }
        newSubcategories[subjectIndex] = [...currentSubcats, subcategory];
      }
      
      return {
        ...prev,
        subject_subcategories: newSubcategories
      };
    });
    
    // Clear error for this subcategory
    if (errors[`subject_subcategory_${subjectIndex}`]) {
      setErrors(prev => ({ ...prev, [`subject_subcategory_${subjectIndex}`]: null }));
    }
  };

  const handleAddLanguage = () => {
    setFormData(prev => ({
      ...prev,
      languages: [...prev.languages, { language: '', level: '' }]
    }));
  };

  const handleRemoveLanguage = (index) => {
    setFormData(prev => ({
      ...prev,
      languages: prev.languages.filter((_, i) => i !== index)
    }));
  };

  const handleLanguageChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      languages: prev.languages.map((lang, i) => 
        i === index ? { ...lang, [field]: value } : lang
      )
    }));
  };

  const handleGradeToggle = (grade) => {
    setFormData(prev => {
      const currentGrades = prev.grade_levels || [];
      if (currentGrades.includes(grade)) {
        return { ...prev, grade_levels: currentGrades.filter(g => g !== grade) };
      } else {
        return { ...prev, grade_levels: [...currentGrades, grade] };
      }
    });
    // Clear error for this field
    if (errors.grade_levels) {
      setErrors(prev => ({ ...prev, grade_levels: null }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) {
      return;
    }

    setLoading(true);
    
    try {
      // Save draft
      await onSave(formData);
      
      // Continue to next step
      onNext(formData);
    } catch (error) {
      console.error('Error saving:', error);
      setErrors({ submit: isArabic ? 'فشل الحفظ. يرجى المحاولة مرة أخرى.' : 'Failed to save. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const displayLanguages = isArabic ? LANGUAGES_AR : LANGUAGES;
  const displayLanguageLevels = isArabic ? LANGUAGE_LEVELS_AR : LANGUAGE_LEVELS;
  const displaySubjects = isArabic ? SUBJECTS_AR : SUBJECTS;

  return (
    <div>
      <h2 className="step-title">{t.about || 'About'}</h2>
      <p className="step-subtitle">
        {t.aboutSubtitle || 'Tell us a bit about yourself. We\'ll use this information to help students find you.'}
      </p>

      <form className="wizard-form" onSubmit={handleSubmit}>
        {/* Full Name */}
        <div className="form-group">
          <label>{t.fullName || 'Full Name'} *</label>
          <input
            type="text"
            name="full_name"
            value={formData.full_name}
            onChange={handleChange}
            placeholder={isArabic ? 'أدخل اسمك الكامل' : 'Enter your full name'}
            disabled={loading}
          />
          {errors.full_name && <span className="error-message">{errors.full_name}</span>}
        </div>

        {/* Email */}
        <div className="form-group">
          <label>{t.emailAddress || 'Email Address'} *</label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder={isArabic ? 'أدخل بريدك الإلكتروني' : 'Enter your email'}
            disabled={loading || !!user} // Lock if from auth user
            readOnly={!!user}
          />
          {user && <span style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
            {t.emailFromAccount || 'Email from your account'}
          </span>}
          {errors.email && <span className="error-message">{errors.email}</span>}
        </div>

        {/* Phone */}
        <div className="form-group">
          <label>{t.phoneNumber || 'Phone Number'} *</label>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'stretch' }}>
            <div style={{ 
              padding: '0 16px', 
              background: '#f3f4f6', 
              border: '1px solid #e5e7eb',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              fontSize: '15px',
              fontWeight: '500',
              direction: 'ltr',
              unicodeBidi: 'isolate',
              height: '44px'
            }}>
              +973
            </div>
            <input
              type="text"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="12345678"
              maxLength={8}
              style={{ flex: 1 }}
              disabled={loading}
            />
          </div>
          {errors.phone && <span className="error-message">{errors.phone}</span>}
        </div>

        {/* Date of Birth */}
        <div className="form-group">
          <label>{isArabic ? 'تاريخ الميلاد' : 'Date of Birth'} *</label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1fr', gap: '12px' }}>
            <CustomDropdown
              name="date_of_birth_day"
              value={formData.date_of_birth_day}
              onChange={handleChange}
              disabled={loading}
              placeholder={isArabic ? 'اليوم' : 'Day'}
              isArabic={isArabic}
              options={[
                { value: '', label: isArabic ? 'اليوم' : 'Day' },
                ...Array.from({ length: 31 }, (_, i) => ({
                  value: String(i + 1),
                  label: String(i + 1)
                }))
              ]}
            />
            <CustomDropdown
              name="date_of_birth_month"
              value={formData.date_of_birth_month}
              onChange={handleChange}
              disabled={loading}
              placeholder={isArabic ? 'الشهر' : 'Month'}
              isArabic={isArabic}
              options={[
                { value: '', label: isArabic ? 'الشهر' : 'Month' },
                ...(isArabic ? [
                  { value: 'January', label: 'يناير' },
                  { value: 'February', label: 'فبراير' },
                  { value: 'March', label: 'مارس' },
                  { value: 'April', label: 'أبريل' },
                  { value: 'May', label: 'مايو' },
                  { value: 'June', label: 'يونيو' },
                  { value: 'July', label: 'يوليو' },
                  { value: 'August', label: 'أغسطس' },
                  { value: 'September', label: 'سبتمبر' },
                  { value: 'October', label: 'أكتوبر' },
                  { value: 'November', label: 'نوفمبر' },
                  { value: 'December', label: 'ديسمبر' }
                ] : [
                  { value: 'January', label: 'January' },
                  { value: 'February', label: 'February' },
                  { value: 'March', label: 'March' },
                  { value: 'April', label: 'April' },
                  { value: 'May', label: 'May' },
                  { value: 'June', label: 'June' },
                  { value: 'July', label: 'July' },
                  { value: 'August', label: 'August' },
                  { value: 'September', label: 'September' },
                  { value: 'October', label: 'October' },
                  { value: 'November', label: 'November' },
                  { value: 'December', label: 'December' }
                ])
              ]}
            />
            <CustomDropdown
              name="date_of_birth_year"
              value={formData.date_of_birth_year}
              onChange={handleChange}
              disabled={loading}
              placeholder={isArabic ? 'السنة' : 'Year'}
              isArabic={isArabic}
              options={[
                { value: '', label: isArabic ? 'السنة' : 'Year' },
                ...Array.from({ length: 100 }, (_, i) => {
                  const year = new Date().getFullYear() - i;
                  return { value: String(year), label: String(year) };
                })
              ]}
            />
          </div>
          {(errors.date_of_birth_day || errors.date_of_birth_month || errors.date_of_birth_year) && (
            <span className="error-message">
              {errors.date_of_birth_day || errors.date_of_birth_month || errors.date_of_birth_year}
            </span>
          )}
        </div>

        {/* Gender */}
        <div className="form-group">
          <label>{isArabic ? 'الجنس' : 'Gender'} *</label>
          <CustomDropdown
            name="gender"
            value={formData.gender}
            onChange={handleChange}
            disabled={loading}
            placeholder={isArabic ? 'اختر الجنس' : 'Select gender'}
            isArabic={isArabic}
            options={[
              { value: '', label: isArabic ? 'اختر الجنس' : 'Select gender' },
              { value: 'Male', label: isArabic ? 'ذكر' : 'Male' },
              { value: 'Female', label: isArabic ? 'أنثى' : 'Female' }
            ]}
          />
          {errors.gender && <span className="error-message">{errors.gender}</span>}
        </div>

        {/* Subject - Multi-select with subcategories */}
        <div className="form-group">
          <label>{t.subjectYouTeach || 'Subject You Teach'} * {formData.subjects && formData.subjects.length > 0 && `(${formData.subjects.length}/3)`}</label>
          <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '12px' }}>
            {isArabic ? 'يمكنك اختيار ما يصل إلى 3 مواد' : 'You can select up to 3 subjects'}
          </p>
          
          {/* Selected Subjects */}
          {formData.subjects && formData.subjects.length > 0 && (
            <div style={{ marginBottom: '16px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {formData.subjects.map((subject, idx) => {
                const subjectIndex = SUBJECTS.indexOf(subject);
                let subjectLabel = isArabic ? SUBJECTS_AR[subjectIndex] : subject;
                
                // If "Other", show the custom name if available
                if (subject === 'Other' && formData.other_subject_names?.[idx]) {
                  subjectLabel = formData.other_subject_names[idx];
                }
                
                const hasSubcategories = SUBJECT_SUBCATEGORIES[subject];
                
                return (
                  <div key={idx} style={{
                    padding: '8px 12px',
                    background: '#fff4e6',
                    border: '2px solid #ff7a1a',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <span style={{ fontSize: '14px', fontWeight: '500', color: '#1f2937' }}>
                      {subjectLabel}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleSubjectToggle(subject)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#ff7a1a',
                        cursor: 'pointer',
                        padding: '0',
                        fontSize: '16px',
                        lineHeight: 1
                      }}
                    >
                      ×
                    </button>
                  </div>
                );
              })}
            </div>
          )}
          
          {/* Subject Selection Dropdown */}
          {(!formData.subjects || formData.subjects.length < 3) && (
            <CustomDropdown
              name="subject"
              value=""
              onChange={(e) => {
                if (e.target.value && !formData.subjects?.includes(e.target.value)) {
                  handleSubjectToggle(e.target.value);
                }
              }}
              disabled={loading}
              placeholder={isArabic ? 'اختر المادة' : 'Select a subject'}
              isArabic={isArabic}
              options={[
                { value: '', label: isArabic ? 'اختر المادة' : 'Select a subject' },
                ...SUBJECTS.map((subject, idx) => ({
                  value: subject,
                  label: displaySubjects[idx],
                  disabled: formData.subjects?.includes(subject)
                }))
              ]}
            />
          )}
          
          {errors.subjects && <span className="error-message">{errors.subjects}</span>}
          
          {/* Subcategories for selected subjects */}
          {formData.subjects && formData.subjects.map((subject, subjectIdx) => {
            const subcategories = SUBJECT_SUBCATEGORIES[subject];
            if (!subcategories) return null;
            
            const subcategoriesAR = SUBJECT_SUBCATEGORIES_AR[subject] || subcategories;
            const selectedSubcats = formData.subject_subcategories?.[subjectIdx] || [];
            const subjectIndex = SUBJECTS.indexOf(subject);
            const subjectLabel = isArabic ? SUBJECTS_AR[subjectIndex] : subject;
            
            return (
              <div key={subjectIdx} style={{ marginTop: '20px', padding: '16px', background: '#f9fafb', borderRadius: '12px', border: '1px solid #e5e7eb' }}>
                <label style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937', marginBottom: '12px', display: 'block' }}>
                  {isArabic ? `نوع ${subjectLabel} (اختياري، حد أقصى 3)` : `Type of ${subject} (optional, max 3)`}
                  {selectedSubcats.length > 0 && ` (${selectedSubcats.length}/3)`}
                </label>
                
                {/* Selected Subcategories */}
                {selectedSubcats.length > 0 && (
                  <div style={{ marginBottom: '12px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {selectedSubcats.map((subcat, subcatIdx) => {
                      const subcatIndex = subcategories.indexOf(subcat);
                      const subcatLabel = isArabic ? subcategoriesAR[subcatIndex] : subcat;
                      return (
                        <div key={subcatIdx} style={{
                          padding: '6px 10px',
                          background: '#e0f2fe',
                          border: '1px solid #0ea5e9',
                          borderRadius: '6px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          fontSize: '13px'
                        }}>
                          <span>{subcatLabel}</span>
                          <button
                            type="button"
                            onClick={() => handleSubcategoryToggle(subjectIdx, subcat)}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: '#0ea5e9',
                              cursor: 'pointer',
                              padding: '0',
                              fontSize: '14px',
                              lineHeight: 1
                            }}
                          >
                            ×
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
                
                {/* Subcategory Selection */}
                {selectedSubcats.length < 3 && (
                  <CustomDropdown
                    name={`subject_subcategory_${subjectIdx}`}
                    value=""
                    onChange={(e) => {
                      if (e.target.value && !selectedSubcats.includes(e.target.value)) {
                        handleSubcategoryToggle(subjectIdx, e.target.value);
                      }
                    }}
                    disabled={loading}
                    placeholder={isArabic ? 'اختر النوع' : 'Select type'}
                    isArabic={isArabic}
                    options={[
                      { value: '', label: isArabic ? 'اختر النوع' : 'Select type' },
                      ...subcategories.map((subcat, idx) => ({
                        value: subcat,
                        label: isArabic ? subcategoriesAR[idx] : subcat,
                        disabled: selectedSubcats.includes(subcat)
                      }))
                    ]}
                  />
                )}
                
                {errors[`subject_subcategory_${subjectIdx}`] && (
                  <span className="error-message" style={{ marginTop: '8px', display: 'block' }}>
                    {errors[`subject_subcategory_${subjectIdx}`]}
                  </span>
                )}
              </div>
            );
          })}
          
          {/* "Other" subject name input */}
          {formData.subjects && formData.subjects.map((subject, subjectIdx) => {
            if (subject !== 'Other') return null;
            
            const otherName = formData.other_subject_names?.[subjectIdx] || '';
            
            return (
              <div key={`other_${subjectIdx}`} style={{ marginTop: '20px', padding: '16px', background: '#f9fafb', borderRadius: '12px', border: '1px solid #e5e7eb' }}>
                <label style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937', marginBottom: '12px', display: 'block' }}>
                  {isArabic ? 'اسم المادة *' : 'Subject Name *'}
                </label>
                <input
                  type="text"
                  value={otherName}
                  onChange={(e) => handleOtherSubjectNameChange(subjectIdx, e.target.value)}
                  placeholder={isArabic ? 'أدخل اسم المادة' : 'Enter subject name'}
                  disabled={loading}
                  style={{
                    width: '100%',
                    height: '44px',
                    padding: '0 16px',
                    border: errors[`other_subject_${subjectIdx}`] ? '1px solid #dc2626' : '1px solid #e5e7eb',
                    borderRadius: '12px',
                    fontSize: '15px',
                    fontFamily: 'inherit',
                    background: 'white',
                    color: '#1f2937',
                    transition: 'all 0.2s'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#037EF3';
                    e.target.style.boxShadow = '0 0 0 2px rgba(3, 126, 243, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = errors[`other_subject_${subjectIdx}`] ? '#dc2626' : '#e5e7eb';
                    e.target.style.boxShadow = 'none';
                  }}
                />
                {errors[`other_subject_${subjectIdx}`] && (
                  <span className="error-message" style={{ marginTop: '8px', display: 'block' }}>
                    {errors[`other_subject_${subjectIdx}`]}
                  </span>
                )}
              </div>
            );
          })}
        </div>

        {/* Languages */}
        <div className="form-group">
          <label>{t.languagesYouSpeak || 'Languages You Speak'} *</label>
          {formData.languages.map((lang, index) => (
            <div key={index} style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
              <div style={{ flex: 1 }}>
                <CustomDropdown
                  value={lang.language || ''}
                  onChange={(e) => handleLanguageChange(index, 'language', e.target.value)}
                  disabled={loading}
                  placeholder={isArabic ? 'اختر اللغة' : 'Select language'}
                  isArabic={isArabic}
                  options={[
                    { value: '', label: isArabic ? 'اختر اللغة' : 'Select language' },
                    ...LANGUAGES.map((l, idx) => ({
                      value: l,
                      label: displayLanguages[idx]
                    }))
                  ]}
                />
              </div>
              <div style={{ flex: 1 }}>
                <CustomDropdown
                  value={lang.level || ''}
                  onChange={(e) => handleLanguageChange(index, 'level', e.target.value)}
                  disabled={loading}
                  placeholder={isArabic ? 'اختر المستوى' : 'Select level'}
                  isArabic={isArabic}
                  options={[
                    { value: '', label: isArabic ? 'اختر المستوى' : 'Select level' },
                    ...LANGUAGE_LEVELS.map((level, idx) => ({
                      value: level,
                      label: displayLanguageLevels[idx]
                    }))
                  ]}
                />
              </div>
              {formData.languages.length > 1 && (
                <button
                  type="button"
                  onClick={() => handleRemoveLanguage(index)}
                  disabled={loading}
                  style={{
                    padding: '8px 12px',
                    border: '1px solid #dc2626',
                    borderRadius: '8px',
                    background: 'white',
                    color: '#dc2626',
                    cursor: 'pointer'
                  }}
                >
                  <FaTimes />
                </button>
              )}
            </div>
          ))}
          
          <button
            type="button"
            onClick={handleAddLanguage}
            disabled={loading}
            style={{
              padding: '10px 16px',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              background: 'white',
              color: '#374151',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            <FaPlus /> {t.addLanguage || 'Add Language'}
          </button>
          
          {errors.languages && <span className="error-message">{errors.languages}</span>}
        </div>

        {/* Grade Levels */}
        <div className="form-group">
          <label>{t.gradesYouTeach || 'Grade Levels You Teach'} *</label>
          <div ref={gradesDropdownRef} style={{ position: 'relative' }}>
            <div
              onClick={() => setGradesDropdownOpen(!gradesDropdownOpen)}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: gradesDropdownOpen ? '2px solid #ff7a1a' : '1px solid #e5e7eb',
                borderRadius: '12px',
                background: 'white',
                color: formData.grade_levels.length > 0 ? '#1f2937' : '#9ca3af',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                minHeight: '44px',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                if (!gradesDropdownOpen) {
                  e.currentTarget.style.borderColor = '#d1d5db';
                }
              }}
              onMouseLeave={(e) => {
                if (!gradesDropdownOpen) {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                }
              }}
            >
              <span>
                {formData.grade_levels.length > 0
                  ? formData.grade_levels.map(grade => {
                      const gradeIndex = SCHOOL_GRADES.indexOf(grade);
                      const displayGrade = isArabic
                        ? (gradeIndex !== -1 ? SCHOOL_GRADES_AR[gradeIndex] : UNIVERSITY_GRADES_AR[UNIVERSITY_GRADES.indexOf(grade)])
                        : grade;
                      return displayGrade;
                    }).join(', ')
                  : (isArabic ? 'اختر المستويات' : 'Select grade levels')}
              </span>
              <svg 
                width="16" 
                height="16" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke={gradesDropdownOpen ? '#ff7a1a' : '#6b7280'} 
                strokeWidth="2"
                style={{
                  transform: gradesDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s'
                }}
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </div>
            {gradesDropdownOpen && (
              <div
                style={{
                  position: 'absolute',
                  top: 'calc(100% + 8px)',
                  left: 0,
                  right: 0,
                  background: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '12px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                  zIndex: 1000,
                  maxHeight: '220px', // Show approximately 5 options
                  overflowY: 'auto',
                  padding: '8px'
                }}
              >
                {(isArabic ? SCHOOL_GRADES_AR.concat(UNIVERSITY_GRADES_AR) : SCHOOL_GRADES.concat(UNIVERSITY_GRADES)).map((grade, idx) => {
                  const gradeValue = isArabic 
                    ? (idx < SCHOOL_GRADES_AR.length ? SCHOOL_GRADES[idx] : UNIVERSITY_GRADES[idx - SCHOOL_GRADES_AR.length])
                    : grade;
                  const isSelected = formData.grade_levels.includes(gradeValue);
                  return (
                    <label
                      key={gradeValue}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '12px 16px',
                        cursor: 'pointer',
                        borderRadius: '8px',
                        background: isSelected ? '#fff4e6' : 'transparent',
                        color: isSelected ? '#ff7a1a' : '#1f2937',
                        fontWeight: isSelected ? '600' : '400',
                        fontSize: '15px',
                        transition: 'all 0.15s',
                        marginBottom: '4px'
                      }}
                      onMouseEnter={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.background = '#f9fafb';
                          e.currentTarget.style.color = '#1f2937';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.background = 'transparent';
                          e.currentTarget.style.color = '#1f2937';
                        }
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleGradeToggle(gradeValue);
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                        style={{ 
                          marginRight: '12px', 
                          width: '18px', 
                          height: '18px', 
                          cursor: 'pointer',
                          accentColor: '#ff7a1a',
                          flexShrink: 0
                        }}
                      />
                      <span onClick={(e) => e.stopPropagation()}>{grade}</span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
          {formData.grade_levels.length > 0 && (
            <div style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {formData.grade_levels.map((grade) => {
                const gradeIndex = SCHOOL_GRADES.indexOf(grade);
                const displayGrade = isArabic
                  ? (gradeIndex !== -1 ? SCHOOL_GRADES_AR[gradeIndex] : UNIVERSITY_GRADES_AR[UNIVERSITY_GRADES.indexOf(grade)])
                  : grade;
                return (
                  <span
                    key={grade}
                    style={{
                      padding: '6px 12px',
                      background: '#f3f4f6',
                      borderRadius: '8px',
                      fontSize: '13px',
                      color: '#1f2937',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    {displayGrade}
                    <button
                      type="button"
                      onClick={() => handleGradeToggle(grade)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#6b7280',
                        cursor: 'pointer',
                        padding: 0,
                        fontSize: '16px',
                        lineHeight: 1
                      }}
                    >
                      <FaTimes />
                    </button>
                  </span>
                );
              })}
            </div>
          )}
          {errors.grade_levels && <span className="error-message">{errors.grade_levels}</span>}
        </div>

        {/* Over 18 Checkbox */}
        <div className="form-group">
          <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              name="over_18_confirmed"
              checked={formData.over_18_confirmed}
              onChange={handleChange}
              disabled={loading}
              style={{ width: '18px', height: '18px', cursor: 'pointer' }}
            />
            <span>{t.confirmOver18 || 'I confirm I am over 18 years old'} *</span>
          </label>
          {errors.over_18_confirmed && <span className="error-message">{errors.over_18_confirmed}</span>}
        </div>

        {errors.submit && <div className="error-message">{errors.submit}</div>}

        {/* Actions */}
        <div className="wizard-actions">
          <div></div>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? (isArabic ? 'جاري الحفظ...' : 'Saving...') : (t.saveAndContinue || 'Save and continue')}
          </button>
        </div>
      </form>
    </div>
  );
}